import { BLINK_FLAGS, envFlags } from '@thoughtspot/env-flags';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import { translate as t } from '@thoughtspot/i18n';
import {
    MetricsService,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import {
    isRolesEnabled,
    isWriteBackEnabled as isWriteBackEnabledInCluster,
    PRIVILEGES,
    SessionService,
} from '@thoughtspot/session-service';
import { Subject } from 'rxjs';
import {
    AnswerColumnEffectiveType,
    AnswerFormula,
    CohortType,
    Column,
    FalconDataType,
    InputTableColumn,
    Parameter,
    ParameterOwnerInfo,
    ParameterValueListItem,
    ParameterValueRange,
    ParameterValueType,
} from '/@services/generated/graphql-types';
import { getParameterDisplayValue } from '/@utils/parameter.utils';
import { getCurrentParameterOwner } from '../edit-parameter-dialog/edit-parameter-dialog.util';

export enum DataPanelView {
    POPULAR_COLUMNS = 'POPULAR_COLUMNS',
    BY_CATEGORY = 'BY_CATEGORY',
    A_TO_Z = 'A_TO_Z',
}

export enum DataPanelViewTab {
    // Values must be a valid enum key from `DataPanelView` (if there is a match).
    POPULAR_COLUMNS = 'POPULAR_COLUMNS',
    ALL = 'ALL',
    A_TO_Z = 'A_TO_Z',
    BY_CATEGORY = 'BY_CATEGORY',
}

export enum AllCategorySelection {
    // Values must be a valid enum value from `DataPanelView`.
    BY_CATEGORY = 'BY_CATEGORY',
    A_TO_Z = 'A_TO_Z',
}

export enum AllCategorySelectionLabel {
    // Keys must be a valid enum value from `AllCategorySelection`.
    BY_CATEGORY = 'dataPanelV2.section.allSection.category',
    A_TO_Z = 'dataPanelV2.section.allSection.aToZ',
}

// This emun is specfically used for colouring
// columns and list items
export enum ColumnType {
    ATTRIBUTE = 'ATTRIBUTE',
    MEASURE = 'MEASURE',
    DATE = 'DATE',
    PARAMETER = 'PARAMETER',
    COHORT = 'COHORT',
    UNKNOWN = 'UNKNOWN',
}

export enum CohortOwnerSelection {
    ALL = 'ALL',
    YOURS = 'YOURS',
    ANSWER = 'ANSWER',
}

export enum ObjectCreationOption {
    CREATE_FORMULA = 'CREATE_FORMULA',
    CREATE_PARAMETER = 'CREATE_PARAMETER',
    CREATE_SIMPLE_COHORT = 'CREATE_SIMPLE_COHORT',
    CREATE_ADVANCED_COHORT = 'CREATE_ADVANCED_COHORT',
    CREATE_INPUT_TABLE = 'CREATE_INPUT_TABLE',
}

// Define the order of columns inside a custom group.
// As in case of custom groups we want to show Date column,
// Attribute column and then Measure column.
export const CUSTOM_COLUMN_GROUPS_SORT_ORDER = {
    [ColumnType.DATE]: 3,
    [ColumnType.ATTRIBUTE]: 2,
    [ColumnType.MEASURE]: 1,
    // Unused, added to resolve TS errors and for edge case bugs.
    // Custom group columns are filtered in `prism` to only be the above 3 types.
    [ColumnType.COHORT]: 4,
    [ColumnType.PARAMETER]: 5,
    [ColumnType.UNKNOWN]: 6,
};

// Define the initial state of column custom group accordions.
// Use in TSE config.
export const enum CustomColumnGroupsAccordionState {
    EXPAND_ALL = 'EXPAND_ALL',
    COLLAPSE_ALL = 'COLLAPSE_ALL',
    EXPAND_FIRST = 'EXPAND_FIRST',
}

export const DATA_PANEL_V2_ANIMATION_DURATION_MS = 200;

export const SPACE_APART_FORM_DATA_PANEL_IN_PX = 315;

export const HEIGHT_OF_VIEW_SWITCHER_RADIO_IN_ALL_VIEW = 32;

/**
 * Row container is intentionally 1px larger than the row to avoid jumpiness.
 * Hence, its best to use the container when referring to a list item.
 */
export const LIST_ITEM_V2_CLASS_NAME = 'list-item-v2-module__rowContainer';

export const ACCORDIAN_HEADER_HEIGHT_IN_PX = 48;
// Use virtualized list after the number of columns is all the selected sources
// exceeds this number
export const NUMBER_OF_COLUMNS_THRESHOLD = 600;

export const HEIGHT_OF_ONE_ROW_IN_PX = 36;

// In case window.innerHeight computes to 0;
export const DEFAULT_WINDOW_HEIGHT_IN_PX = 800;

export const WINDOW_HEIGHT = window.innerHeight || DEFAULT_WINDOW_HEIGHT_IN_PX;

export const EXPECTED_MAX_NUMBER_OF_ROWS_TO_RENDER =
    Math.floor(
        (WINDOW_HEIGHT -
            SPACE_APART_FORM_DATA_PANEL_IN_PX -
            4 * ACCORDIAN_HEADER_HEIGHT_IN_PX) /
            HEIGHT_OF_ONE_ROW_IN_PX,
    ) - 2;

// This makes sure that we render atleast 2 columns, if this number computes <= 1
export const MAX_NUMBER_OF_ROWS_TO_RENDER =
    EXPECTED_MAX_NUMBER_OF_ROWS_TO_RENDER > 1
        ? EXPECTED_MAX_NUMBER_OF_ROWS_TO_RENDER
        : 2;

/**
 * Applying line limits to:
 * - ALL dynamic texts.
 * - Most static texts as well to ensure good layout on smaller screens.
 *
 * Not applying line limits to:
 * - Static texts where showing the entire content is desired, like, error messages.
 */
export const MAX_LINES_CONFIG = {
    columnOverview: {
        nameHeading: 5,
        typeAndDataTypeCaptions: 1,
        // Used for all label-value information.
        informationFieldValue: 3,
        parameterDetails: {
            // Where parameters are a list of values.
            listValue: 3,
            // Where parameters are a range of min and max values.
            rangeValue: 5,
            // Where parameters are a list of values but with a different display and item value.
            listTableHeader: 3,
            listTableValue: 5,
        },
        topAnswers: { sectionHeading: 2 },
    },
};

export const DATE_TYPE_DATA_TYPES: string[] = [
    'DATE',
    'DATE_TIME',
    'TIME',
    'DATE_NUM',
    'DATE_NUM_QUARTER_IN_YEAR',
    'DATE_NUM_MONTH_IN_YEAR',
    'DATE_NUM_MONTH_IN_QUARTER',
    'DATE_NUM_WEEK_IN_YEAR',
    'DATE_NUM_WEEK_IN_QUARTER',
    'DATE_NUM_WEEK_IN_MONTH',
    'DATE_NUM_DAY_IN_YEAR',
    'DATE_NUM_DAY_IN_QUARTER',
    'DATE_NUM_DAY_IN_MONTH',
    'DATE_NUM_DAY_OF_WEEK',
    'DATE_NUM_DAY_OF_WEEK_STR',
    'DATE_NUM_HOUR_IN_DAY',
    'DATE_NUM_ABS_YEAR',
    'DATE_NUM_ABS_QUARTER',
    'DATE_NUM_ABS_DAY',
    'DATE_NUM_ABS_MONTH',
    'DATE_NUM_ABS_HOUR',
    'DATE_NUM_ABS_WEEK',
    'DATE_NUM_SEC_IN_DAY',
];

export interface ParameterData {
    displayValue: string;
    valueType: ParameterValueType;
    owner: ParameterOwnerInfo;
    valueList?: ParameterValueListItem[];
    valueRange?: ParameterValueRange;
}

export type CatalogInfo = {
    dc_description?: string;
    dc_trustcheck?: string;
};

export type CatalogConfig = {
    desc_show: boolean;
    tc_show: boolean;
};

export interface DataSourceItem {
    id: string;
    name: string;
    author?: string;
    authorDisplayName?: string;
    ownerName?: string;
    isFormula: boolean;
    dataSourceId: string;
    columnType: ColumnType;
    dataType: FalconDataType;
    type?: AnswerColumnEffectiveType;
    description?: string;
    synonyms?: string[];
    sourceId?: string;
    cohortType?: CohortType;
    cohortAnswerId?: string;
    parameterData?: ParameterData;
    catalogInfo?: CatalogInfo;
    desc_show?: boolean;
    tc_show?: boolean;
    isEditable?: boolean;
    displayName?: string;
    isMandatory?: boolean;
    isStrictDateColumn?: boolean;
    isHidden?: boolean;
    isAnswerLevelCohort?: boolean;
    // True for aggregated phrases (HavingPhrase, OfFilterPhrase, etc.) — lets
    // AnswerFilterContainer find the existing aggregated FilterGroup.
    isAggregateApplied?: boolean;
    // Required for formula-backed FilterGroups (LOD phrases); matched only
    // by answerColumnId, not logicalColumnId.
    answerColumnId?: string;
}

// Convert a column or a formula to a uniform data panel item type
export const getDataPanelItemColumnType: (
    item: Column | AnswerFormula,
) => ColumnType = item => {
    if (
        // eslint-disable-next-line no-underscore-dangle
        item.__typename === 'Column'
    ) {
        if (item.type === ColumnType.MEASURE) {
            return ColumnType.MEASURE;
        }
        if (
            item.type === ColumnType.ATTRIBUTE &&
            !DATE_TYPE_DATA_TYPES.includes(item.dataType)
        ) {
            return ColumnType.ATTRIBUTE;
        }
    }
    if (
        // eslint-disable-next-line no-underscore-dangle
        item.__typename === 'AnswerFormula'
    ) {
        if ((item.columnType as string) === ColumnType.MEASURE) {
            return ColumnType.MEASURE;
        }
        if (
            (item.columnType as string) === ColumnType.ATTRIBUTE &&
            !DATE_TYPE_DATA_TYPES.includes(item.dataType)
        ) {
            return ColumnType.ATTRIBUTE;
        }
    }
    return ColumnType.DATE;
};

/**
 * Chip colour for a writeback input-table column in the data panel. Callosum
 * reports the column's kind on the storable (see the InputTableColumn type in
 * prism); without it every input column used to render as an ATTRIBUTE, so a
 * DATE input column showed blue in the panel while its search token was
 * purple. UNKNOWN (callosum reported no kind) falls back to ATTRIBUTE.
 */
export const getInputTableColumnItemType = (
    column: Pick<InputTableColumn, 'dataType' | 'type'>,
): ColumnType => {
    if (column.type === AnswerColumnEffectiveType.Measure) {
        return ColumnType.MEASURE;
    }
    if (column.dataType && DATE_TYPE_DATA_TYPES.includes(column.dataType)) {
        return ColumnType.DATE;
    }
    return ColumnType.ATTRIBUTE;
};

export const getParameterItemType = (parameter: Parameter) => {
    // To color parameter chips
    if (
        parameter.dataType === FalconDataType.Date ||
        parameter.dataType === FalconDataType.DateTime ||
        parameter.dataType === FalconDataType.Time
    ) {
        return ColumnType.DATE;
    }
    if (
        parameter.dataType === FalconDataType.Char ||
        parameter.dataType === FalconDataType.Bool
    ) {
        return ColumnType.ATTRIBUTE;
    }
    if (
        parameter.dataType === FalconDataType.Double ||
        parameter.dataType === FalconDataType.Float ||
        parameter.dataType === FalconDataType.Int32 ||
        parameter.dataType === FalconDataType.Int64
    ) {
        return ColumnType.MEASURE;
    }
    return ColumnType.UNKNOWN;
};

/**
 * Event name: 'click-on-view-switcher'
 * Tracked when user clicks on view switcher.
 */
export const collectViewSwitcherEvent = (
    metricsService: MetricsService,
    getAnswerMixpanelProperties: () => any,
    sourceViewName: DataPanelView,
    targetViewName: DataPanelView,
) => {
    const answerCommonProperties = getAnswerMixpanelProperties
        ? getAnswerMixpanelProperties()
        : {};
    metricsService.collectEvent(
        new UserAction(userActionTypeEnum.CLICK_ON_VIEW_SWITCHER),
        {
            sourceViewName,
            targetViewName,
            ...answerCommonProperties,
        },
    );
};

/**
 * Event name: 'object-creation-finished'
 * Tracked when user creates any object from data panel and saves it
 */
export const collectObjectCreationFinishedEvent = (
    metricsService: MetricsService,
    getAnswerMixpanelProperties: () => any,
    objectCreationOption: ObjectCreationOption,
    isWorksheet = false,
) => {
    const answerCommonProperties = getAnswerMixpanelProperties
        ? getAnswerMixpanelProperties()
        : {};
    metricsService.collectEvent(
        new UserAction(userActionTypeEnum.OBJECT_CREATION_FINISHED),
        {
            objectCreationOption,
            ...answerCommonProperties,
            isWorksheet,
        },
    );
};

/**
 * Event name: 'create-object-from-left-panel-started'
 * Tracked when user starts any object creation from data panel
 */
export const collectCreateObjectFromLeftPanelStartedEvent = (
    metricsService: MetricsService,
    getAnswerMixpanelProperties: () => any,
    objectCreationOption: ObjectCreationOption,
) => {
    const answerCommonProperties = getAnswerMixpanelProperties
        ? getAnswerMixpanelProperties()
        : {};
    metricsService.collectEvent(
        new UserAction(
            userActionTypeEnum.CREATE_OBJECT_FROM_LEFT_PANEL_STARTED,
        ),
        {
            objectCreationOption,
            ...answerCommonProperties,
        },
    );
};

/**
 * Event name: 'custom-column-groups-loaded-in-left-panel'
 * Tracked when user lands on a page with the data panel and a custom column
 *   groups enabled source.
 * Also, when user changes source to a custom column groups enabled source.
 */
export const collectCustomColumnGroupsLoadedInLeftPanelEvent = (
    metricsService: MetricsService,
    getAnswerMixpanelProperties: () => any,
    sourceId: string,
    numOfGroups: number,
    numOfColumnsInEachGroup: number[],
    initialAccordionState: string,
) => {
    metricsService.collectEvent(
        new UserAction(
            userActionTypeEnum.CUSTOM_COLUMN_GROUPS_LOADED_IN_LEFT_PANEL,
        ),
        {
            sourceId,
            numOfGroups,
            numOfColumnsInEachGroup,
            initialAccordionState,
            ...getAnswerMixpanelProperties(),
        },
    );
};

export const convertParameterToDataSourceItem = (
    parameter: Parameter,
    dataSourceId: string,
    answerId?: string,
): DataSourceItem => {
    return {
        id: parameter.id,
        name: parameter.name,
        isFormula: false,
        dataSourceId,
        columnType: ColumnType.PARAMETER,
        dataType: parameter.dataType,
        description: parameter.description,
        parameterData: {
            valueList: parameter.valueList,
            owner: parameter.owner,
            valueRange: parameter.valueRange,
            displayValue: getParameterDisplayValue(parameter),
            valueType: parameter.valueType,
        },
        ownerName:
            answerId === parameter.owner.id
                ? getCurrentParameterOwner(parameter.owner.type)
                : parameter.owner.name,
    };
};

export const DESCRIPTION_MAX_HEIGHT_PX = 60; // 3 lines of description

export const openChooseSourcesModalSubject = new Subject();

export const closeChooseSourcesModalSubject = new Subject();

export const getCustomDataErrorModalTitle = (errorCode: number): string => {
    switch (errorCode) {
        case 10002:
            return t('dataPanel.customData.error.code.10002');
        case 10003:
            return t('dataPanel.customData.error.code.10003');
        case 12100:
            return t('dataPanel.customData.error.code.12100');
        default:
            return t('ERROR');
    }
};

// Launch Darkly flag for copy formula
export const isCopyFormulaEnabled = () => {
    return isFeatureEnabled(FEATURE_FLAG_NAMES.ENABLE_COPY_FORMULA, {
        urlParamValue: envFlags?.getValue(
            BLINK_FLAGS.isCopyFormulaEnabled.name,
        ),
    });
};

// Launch Darkly flag for copy parameter
export const isCopyParameterEnabled = () => {
    return isFeatureEnabled(FEATURE_FLAG_NAMES.ENABLE_COPY_PARAMETER);
};

export const systemUserName = 'system';

// Whether input tables are visible at all — flag-only, no privilege check.
// Editing and managing them stay privilege-gated via canEditInputTables /
// canManageInputTables below.
export const isInputTableVisible = () => isWriteBackEnabledInCluster();

// With roles off the server checks only "Can manage data", UI is intentionally
// made stricter.
const hasRolesOffDataPrivilege = (sessionService: SessionService) =>
    isRolesEnabled() ||
    !!sessionService.getUserPrivileges()?.includes(PRIVILEGES.DATAMANAGEMENT);

// Privilege to create, delete and fully manage input tables.
// Deliberately not exported — callers must go through canManageInputTables so
// the cluster-level gate is never skipped.
const hasManageInputTablePrivilege = (sessionService: SessionService) => {
    const privileges = sessionService.getUserPrivileges();
    if (privileges?.includes(PRIVILEGES.ADMINISTRATION)) {
        return true;
    }
    return (
        !!privileges?.includes(PRIVILEGES.CAN_MANAGE_INPUT_TABLES) &&
        hasRolesOffDataPrivilege(sessionService)
    );
};

// Privilege to edit existing input tables. Users who can manage input tables
// can obviously edit them too, so this is a superset of the privilege above.
// Deliberately not exported — see the note on hasManageInputTablePrivilege.
const hasEditInputTablePrivilege = (sessionService: SessionService) =>
    hasManageInputTablePrivilege(sessionService) ||
    (!!sessionService
        .getUserPrivileges()
        ?.includes(PRIVILEGES.CAN_EDIT_INPUT_TABLES) &&
        hasRolesOffDataPrivilege(sessionService));

// Whether the user may edit existing input tables — editing cell values and
// opening the input table edit panel. Satisfied by either privilege, since
// CAN_MANAGE_INPUT_TABLES implies edit rights. Changing the column set is a
// schema change, gated separately by canManageInputTables.
export const canEditInputTables = (sessionService: SessionService) =>
    isWriteBackEnabledInCluster() && hasEditInputTablePrivilege(sessionService);

// Whether the user may create or delete input tables, and add or delete their
// input columns. Strictly narrower than canEditInputTables:
// CAN_EDIT_INPUT_TABLES alone is not enough.
export const canManageInputTables = (sessionService: SessionService) =>
    isWriteBackEnabledInCluster() &&
    hasManageInputTablePrivilege(sessionService);

// Whether the write-back surfaces (the data panel "Custom data" section, the
// input table edit panel, cell editing) are available at all. Being able to
// edit input tables is what makes them useful, so this is the edit gate under
// its feature-level name — creating and deleting are gated separately by
// canManageInputTables.
export const isWriteBackEnabled = canEditInputTables;
